import React, { useEffect, useState } from 'react';
import FormWrapper from '../../../../../Components/Form/FormWrapper';
import InputWrapper from '../../../../../Components/Form/components/InputWrapper/InputWrapper';
import SubmitButton from '../../../../../Components/Button/SubmitButton';
import { useForm } from 'react-hook-form';
import NCZIExtensionSearchSelectField from '../../ExtensionSearchSelect/NCZIExtensionSearchSelectField';
import { NCZI_OID } from '../../../Enums/ncziOid.enum';
import SelectField from '../../../../../Components/Form/components/SelectField';
import Button from '../../../../../Components/Button/Button';
import { Theme } from '../../../../../Enums/Theme.enum';
import apiClient from '../../../../../Services/ApiClient';
import useNotificationHook from '../../../../../Hooks/useNotification.hook';
import useTranslation from '../../../../../Hooks/useTranslation';
import useNCZIDefaultValues from '../../../Hooks/useNCZIDefaultValues';
import Loading from '../../../../../Components/Loading';
import { NcziOupZsItem, useLazyFindOupzsQuery } from '../../../../../graphql/graphql';
import { useEffectAsync } from '../../../../../helpers';
import { NCZIExtensionSelectItem, NCZIExtensionValidator } from '../../ExtensionSearchSelect/NCZIExtensionSearchSelect';
import { NCZIReferenceSelectItem, NCZIReferenceValidator } from '../../ReferenceSearchSelect/NCZIReferenceSearchSelect';
import { NCZIExtension, NCZIReference } from '../../../Interfaces/ncziTypes';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

interface Props {
	onSuccess: (data: NCZISetupGeneralInformationData) => void;
}

const validationSchema = yup.object({
	healthProfessionalCode: NCZIExtensionValidator('component.nczi.setup.healthProfessional'),
	oupzs: NCZIExtensionValidator('component.nczi.setup.oupzs')
		.nullable()
		.test('is-null', 'component.nczi.setup.oupzs', value => value !== null),
	specialization: NCZIReferenceValidator('component.nczi.setup.specialization')
		.nullable()
		.test('is-null', 'component.nczi.setup.specialization', value => value !== null)
});

interface NCZISetupGeneralInformationValues {
	healthProfessionalCode: NCZIExtensionSelectItem;
	oupzs: NCZIExtensionSelectItem;
	specialization: NCZIReferenceSelectItem;
}

export interface NCZISetupGeneralInformationData {
	specialization: NCZIReference;
	healthServiceProvider: NCZIExtension;
	healthProfessionalCode: string;
	serviceProvider: NCZIExtension;
	oupzsZprCode: string;
}

const NCZISetupGeneralInformation: React.FC<Props> = ({ onSuccess }) => {
	const formMethods = useForm({
		resolver: yupResolver(validationSchema)
	});

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const {showNotificationSuccess, showNotificationAlert} = useNotificationHook();
	const {t} = useTranslation();
	const {extensionDefaultValue} = useNCZIDefaultValues();
	const [findOupzs] = useLazyFindOupzsQuery();
	const [oupzsItems, setOupzsItems] = useState<NCZIExtensionSelectItem[]>([]);
	const [specializationItems, setSpecializationItems] = useState<NCZIReferenceSelectItem[]>([]);
	const [professionalOupzsItems, setProfessionalOupzsItems] = useState<NcziOupZsItem[]|undefined>(undefined);

	const loadFromCard = async () => {
		try {
			setIsLoading(true);
			const { data } = await apiClient.post('/nczi/communicate', {
				eventName: 'readCard',
			});

			if (data.res?.card?.healthProfessionalCode) {
				const referenceSelect = await extensionDefaultValue({
					code: data.res.card.healthProfessionalCode,
					scheme: {
						oid: NCZI_OID.HEALTH_PROFESSIONAL,
					},
					valid: undefined,
				});

				formMethods.setValue('healthProfessionalCode', referenceSelect);
				showNotificationSuccess({ content: t('component.nczi.success') });
			} else {
				showNotificationAlert({
					content: data.res?.error
						? data.res.error
						: t('component.nczi.setup.couldNotLoadData'),
				});
			}
		} catch (e: any) {
			if (e.response?.data.displayableError) {
				showNotificationAlert({
					content: e.response.data.message,
				});
			}
		} finally {
			setIsLoading(false);
		}
	};

	const healthProfessionalCodeWatcher = formMethods.watch('healthProfessionalCode');
	useEffectAsync(async () => {
		const tmpProfessionalItems: NcziOupZsItem[] = [];
		const tmpItems: NCZIExtensionSelectItem[] = [];

		if(healthProfessionalCodeWatcher?.value?.code) {
			const { items: oupzsItems } = await findOupzs({
				code: healthProfessionalCodeWatcher?.value?.code
			}).unwrap();

			for(const oupzsItem of oupzsItems ?? []) {
				if(oupzsItem?.extension) {
					tmpItems.push({
						label: oupzsItem.name + (oupzsItem.provider ? ' (' + oupzsItem.provider.name + ')' : ''),
						value: oupzsItem.extension as NCZIExtension
					})
					tmpProfessionalItems.push(oupzsItem);
				}
			}
		}

		setProfessionalOupzsItems(tmpProfessionalItems);
		setOupzsItems(tmpItems);
		setSpecializationItems([]);
	}, [healthProfessionalCodeWatcher]);

	const oupzsWatcher = formMethods.watch('oupzs');
	useEffect(() => {
		const tmpSpecializations = [];

		if(oupzsWatcher?.value?.code) {
			for (const oupzsItem of (professionalOupzsItems ?? [])) {
				if(oupzsItem.code === oupzsWatcher.value.code) {
					for(const specialization of (oupzsItem.specialization ?? [])) {
						if(specialization) {
							tmpSpecializations.push({
								label: specialization.ZPrCode + ' - ' + specialization.specialization.displayName,
								value: specialization.specialization
							});
						}
					}
				}
			}
		}

		setSpecializationItems(tmpSpecializations);
	}, [oupzsWatcher]);

	useEffect(() => {
		formMethods.setValue('oupzs', oupzsItems[0] || null);
	}, [oupzsItems]);

	useEffect(() => {
		formMethods.setValue('specialization', specializationItems[0] || null);
	}, [specializationItems]);

	const handleSubmit = (values: NCZISetupGeneralInformationValues) => {
		for (const oupzsItem of (professionalOupzsItems ?? [])) {
			if (oupzsItem.code === oupzsWatcher.value.code) {
				for(const specialization of (oupzsItem.specialization ?? [])) {
					if(specialization?.specialization.code === values.specialization.value.code) {
						onSuccess({
							healthProfessionalCode: values.healthProfessionalCode.value.code,
							healthServiceProvider: values.oupzs.value,
							specialization: values.specialization.value,
							oupzsZprCode: specialization.ZPrCode,
							serviceProvider: {
								code: oupzsItem.provider?.extension?.code || '',
								scheme: {
									oid: NCZI_OID.HEALTH_SERVICE_PROVIDER
								},
								valid: undefined
							}
						});
						return;
					}
				}
			}
		}
	};

	if(isLoading) {
		return (
			<Loading />
		)
	}

	return (
		<FormWrapper formMethods={formMethods}>
			<InputWrapper>
				<div className='row align-items-end'>
						<div className='col-md-8'>
								<NCZIExtensionSearchSelectField
										name='healthProfessionalCode'
										oid={NCZI_OID.HEALTH_PROFESSIONAL}
										label='component.nczi.setup.healthProfessional'
										isRequired
										showCode
								/>
						</div>
						<div className='col-md-4'>
								<Button
										theme={Theme.BLUE}
										title='component.nczi.setup.loadFromCard'
										onClick={loadFromCard}
								/>
						</div>
				</div>
			</InputWrapper>
			<InputWrapper>
				<SelectField name='oupzs' label='component.nczi.setup.oupzs' options={oupzsItems} isRequired />
			</InputWrapper>
			<InputWrapper>
				<SelectField name='specialization' label='component.nczi.setup.specialization' options={specializationItems} isRequired />
			</InputWrapper>
			<InputWrapper>
				<SubmitButton onSubmit={handleSubmit} title={'component.nczi.setup.save'} />
			</InputWrapper>
		</FormWrapper>
	);
};

export default NCZISetupGeneralInformation;
